<ng-container *ngIf="(isLoading$ | async)">
  <terra-loader class="loading-permission-dialog"></terra-loader>
</ng-container>

<terra-dialog-header [bottomMargin]="false">
  <h1 class="lo_sensitive">
    <ng-container *ngIf="context.createMode">
      Create New User
    </ng-container>
    <ng-container *ngIf="!context.createMode">
      {{ context.userPermissionDto.email }}
    </ng-container>
  </h1>
</terra-dialog-header>

<form [formGroup]="pageForm" id="pageForm" (ngSubmit)="save()" class="terra-error-style" mat-dialog-content
      [ngClass]="{'user-and-permission-dialog-body':true, 'terra-error-style':true, 'loading': isLoading$ | async}">
  <div class="form-container terra-error-style">
    <!-- Name -->
    <section>
      <div [ngClass]="{ 'name-and-email': !!context.createMode }">
        <div [ngClass]="{ 'full-name': true, 'width-field': !context.createMode }">
          <h2 class="title">Name</h2>
          <mat-form-field appearance="outline">
            <input matInput formControlName="name" placeholder="e.g John Smith">
            <mat-error *ngIf="pageForm.get('name').hasError('required')">
              Required
            </mat-error>
          </mat-form-field>
        </div>
        <ng-container *ngIf="!!context.createMode">
          <!-- Email -->
          <div class="email">
            <h2 class="title title-radio-btn">Email</h2>
            <mat-form-field appearance="outline">
              <input matInput type="email" formControlName="email" placeholder="e.g JohnSmith@abc.com">
              <mat-error *ngIf="pageForm.get('email').hasError('required')">
                Required
              </mat-error>
              <mat-error *ngIf="pageForm.get('email').hasError('email')"> Please enter a valid email address</mat-error>
            </mat-form-field>
          </div>
        </ng-container>
      </div>
    </section>

    <!-- Permission Level -->
    <section class="margin-bottom">
      <h2 class="title">
        Role
        <mat-icon matTooltip="Readonly users can only view information but can’t make any changes.
Editors can make changes to any section or holding they have access to.
Admins are the only users who can add/remove users or update permissions."
                  matTooltipClass="terra-tooltip-compact"
                  matTooltipPosition="right">
          help
        </mat-icon>
      </h2>
      <span class="description">
        Determine if the user can perform any changes.
      </span>
      <div class="permission-level radio-button">
        <mat-radio-group formControlName="permissionLevel">
          <mat-radio-button [value]="PermissionLevel.Admin">{{PermissionLevel.Admin | enumString:PermissionLevel}}
          </mat-radio-button>
          <mat-radio-button [value]="PermissionLevel.Editor">{{PermissionLevel.Editor | enumString:PermissionLevel}}
          </mat-radio-button>
          <mat-radio-button [value]="PermissionLevel.Readonly">{{PermissionLevel.Readonly | enumString:PermissionLevel}}
          </mat-radio-button>
        </mat-radio-group>
        <mat-error class="error-field-msg"
                   *ngIf="(isSubmitting$ | async) && pageForm.get('permissionLevel').hasError('required')">
          Required
        </mat-error>
      </div>
    </section>

    <!-- Financial Permission Level -->
    <section class="margin-bottom">
      <h2 class="title">
        Financial Permissions Level
        <mat-icon matTooltip="Determines whether a user can create and authorize payments, distributions, capital calls or create bank accounts.
The Hidden Permission Level will hide all bank account balances from the user and the user won't be able to access Bank Accounts section, but the user will still be able to see contribution and distribution amounts."
                  matTooltipClass="terra-tooltip-compact"
                  matTooltipPosition="right">
          help
        </mat-icon>
      </h2>
      <span class="description">
        Should the user be allowed to view bank accounts? Approve financial actions?
      </span>
      <div class="financial-permission-level radio-button">
        <mat-radio-group formControlName="financialPermissionLevel">
          <mat-radio-button [value]="FinancialPermissionLevel.AuthorizedSignatory" [disabled]="pageForm.get('permissionLevel').value === PermissionLevel.Readonly">
            {{FinancialPermissionLevel.AuthorizedSignatory | enumString:FinancialPermissionLevel}}
          </mat-radio-button>
          <mat-radio-button [value]="FinancialPermissionLevel.NonSignatory">{{FinancialPermissionLevel.NonSignatory |
            enumString:FinancialPermissionLevel}}
          </mat-radio-button>
          <mat-radio-button [value]="FinancialPermissionLevel.Hidden">{{FinancialPermissionLevel.Hidden |
            enumString:FinancialPermissionLevel}}
          </mat-radio-button>
        </mat-radio-group>
        <mat-error class="error-field-msg"
                   *ngIf="(isSubmitting$ | async) && pageForm.get('financialPermissionLevel').hasError('required')">
          Required
        </mat-error>
      </div>
    </section>

    <!-- Contacts Visibility Permission -->
    <section class="margin-bottom">
      <h2 class="title">
        Contacts Visibility Permissions
        <mat-icon matTooltip="Users with Hidden permissions would see a number instead of investors' names and won't be able to access Contacts section.
Other users would see both the name and the investor number."
                  matTooltipClass="terra-tooltip-compact"
                  matTooltipPosition="right">
          help
        </mat-icon>
      </h2>
      <span class="description">
        Should the user be allowed to view real investor names?
      </span>
      <div class="contact-visibility-permission radio-button">
        <mat-radio-group formControlName="contactVisibilityPermission">
          <mat-radio-button [value]="ContactsVisibilityPermission.Visible">{{ContactsVisibilityPermission.Visible |
            enumString:ContactsVisibilityPermission}}
          </mat-radio-button>
          <mat-radio-button [value]="ContactsVisibilityPermission.Hidden">{{ContactsVisibilityPermission.Hidden |
            enumString:ContactsVisibilityPermission}}
          </mat-radio-button>
        </mat-radio-group>
        <mat-error class="error-field-msg"
                   *ngIf="(isSubmitting$ | async) && pageForm.get('contactVisibilityPermission').hasError('required')">
          Required
        </mat-error>
      </div>
    </section>

    <!-- Sections Access -->
    <section>
      <h2 class="title">Sections Access</h2>
      <span class="description">
        To which parts of the platform is the user allowed to access.
      </span>
      <div class="sections-access width-field">
        <mat-form-field>
          <mat-select #selectSectionsAccess multiple formControlName="sectionsAccess">
            <mat-select-trigger>
              <ng-container *ngIf="includeAllSectionsAccess; else notAllSectionsAccess;">
                {{SectionsAccess.All | enumString:SectionsAccess}}
              </ng-container>

              <ng-template #notAllSectionsAccess>
                {{(pageForm.get('sectionsAccess').value ?
                  (pageForm.get('sectionsAccess').value[0] | enumString:SectionsAccess) :
                   SectionsAccess.None | enumString:SectionsAccess)}}
                <span *ngIf="(pageForm.get('sectionsAccess').value?.length || 0) > 1"
                      class="sections-access-additional-selection">
                  (+{{(pageForm.get('sectionsAccess').value?.length || 0) - 1}}
                  {{pageForm.get('sectionsAccess').value?.length === 2 ? 'other' :
                  'others'}})
                </span>
              </ng-template>
            </mat-select-trigger>
            <!-- All -->
            <mat-option (click)="toggleAllSelectionSectionsAccess()" [value]="SectionsAccess.All">
              {{SectionsAccess.All | enumString:SectionsAccess}}
            </mat-option>
            <!-- Funds -->
            <mat-option (click)="optionClickSectionsAccess()" [value]="SectionsAccess.Funds">
              {{SectionsAccess.Funds | enumString:SectionsAccess}}
            </mat-option>
            <!-- Assets -->
            <mat-option (click)="optionClickSectionsAccess()" [value]="SectionsAccess.Assets">
              {{SectionsAccess.Assets | enumString:SectionsAccess}}
            </mat-option>
            <!-- Insights -->
            <mat-option (click)="optionClickSectionsAccess()" [value]="SectionsAccess.Insights">
              {{SectionsAccess.Insights | enumString:SectionsAccess}}
            </mat-option>
            <!-- Positions -->
            <mat-option (click)="optionClickSectionsAccess()" [value]="SectionsAccess.Positions">
              {{SectionsAccess.Positions | enumString:SectionsAccess}}
            </mat-option>
            <!-- Contacts -->
            <mat-option (click)="optionClickSectionsAccess()" [value]="SectionsAccess.Contacts" [disabled]="true"
                         matTooltip="Determined by Contacts Visibility Permission" matTooltipClass="terra-tooltip-compact"
                         matTooltipPosition="above">
              {{SectionsAccess.Contacts | enumString:SectionsAccess}}
            </mat-option>
            <!-- Bank Accounts -->
            <mat-option (click)="optionClickSectionsAccess()" [value]="SectionsAccess.BankAccounts" [disabled]="true"
                        matTooltip="Determined by Financial Permission Level" matTooltipClass="terra-tooltip-compact"
                        matTooltipPosition="above">
              {{SectionsAccess.BankAccounts | enumString:SectionsAccess}}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>
    </section>

    <!-- Holding -->
    <section>
      <div class="holdings width-field">
        <h2 class="title">Holdings</h2>
        <span class="description">
          To which specific holdings is the user allowed to access.
        </span>
        <mat-form-field>
          <mat-select #selectHolding multiple formControlName="holdings">
            <mat-select-trigger>
              <ng-container *ngIf="includeAllHolding; else notAllHolding;">
                All
              </ng-container>

              <ng-template #notAllHolding>
                {{(pageForm.get('holdings').value ?
                getNameOfHolding(pageForm.get('holdings').value[0]) : 'None') | truncate: 30}}
                <span *ngIf="(pageForm.get('holdings').value?.length || 0) > 1" class="holdings-additional-selection">
                  (+{{(pageForm.get('holdings').value?.length || 0) - 1}}
                  {{pageForm.get('holdings').value?.length === 2 ? 'other' :
                  'others'}})
                </span>
              </ng-template>
            </mat-select-trigger>
            <mat-option (click)="toggleAllSelectionHolding()" [value]="-1">
              All
            </mat-option>
            <mat-option (click)="optionClickHolding()" *ngFor="let holding of context.allHoldings; let i = index"
                        [value]="holding.id">
              {{holding.name | truncate: 40:true}}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <ng-container *ngIf="showRentManagerMessage">
          <h2 class="title">Rent Manager Reports</h2>
          <span class="description">
            This user does not have access to all holdings and therefore cannot use the Rent Manager reports integration and attach the Profit & Loss, Balance Sheet, Rent Roll, and Cash Flow reports to quarterly reports.
            <br/><br/>
            Grant access to all holdings to enable this user to attach Rent Manager reports.         
          </span>
        </ng-container>
      </div>
    </section>
  </div>

</form>
<mat-dialog-actions>
  <!-- Error message -->
  <div class="action-container">
    <div class="full-width" *ngIf="isSubmitting$ | async">
      <p *ngIf="pageForm.invalid" class="error-message">
        Please review and correct the fields marked in red
      </p>
    </div>
    <ng-container *ngIf="!(isSubmitting$ | async); else submitting;">
      <div class="buttons">
        <button type="submit" form="pageForm" [disabled]="isLoading$ | async" mat-flat-button
                class="action-btn save-button" color="primary">
          Save
        </button>
        <button type="button" [disabled]="isLoading$ | async" mat-button class="action-btn cancel-button"
                [mat-dialog-close]="undefined">
          Cancel
        </button>
      </div>
    </ng-container>
    <ng-template #submitting>
      <terra-loader></terra-loader>
    </ng-template>
  </div>
</mat-dialog-actions>

<!--<div *ngIf="isSubmitting$ | async" class="full-width">-->
<!--  &lt;!&ndash;      <p *ngIf="pageForm.invalid" class="error-message">&ndash;&gt;-->
<!--  &lt;!&ndash;        Please review and correct the fields marked in red&ndash;&gt;-->
<!--  &lt;!&ndash;      </p>&ndash;&gt;-->
<!--  <p *ngIf="generalServerError" class="error-message">{{ generalServerError }}</p>-->
<!--</div>-->


